import { CustomerService } from '@rightship-tech/common-ui/services/basic/customer-client.service';
import { BindingSignaler } from 'aurelia-templating-resources';
import { AttachmentsHelper } from '@rightship-tech/common-ui/helpers/attachments.helper';
import { AttachmentsClientService } from '@rightship-tech/common-ui/services/attachments/attachments-client.service';
import { autoinject, transient } from "aurelia-dependency-injection";
import Enumerable from 'linq';
import * as moment from 'moment';
import { computedFrom } from "aurelia-framework";
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { FileHelper } from "@rightship-tech/common-ui/helpers/file-helper";
import { RightShipInspectionsService } from "../../../services/rightship-inspection.service";
import { ConfigService } from '@rightship-tech/common-ui/services/basic/config.service';
import { UserClaimsService } from '@rightship-tech/common-ui/services/basic/user-claims.service';
import { CommonUiDialogConstants } from '@rightship-tech/common-ui/constants/dialog.constants';
import { FindingRiskLevelEnum, FleetBenchMarkingChartData, FleetBenchMarkingChartLegend, FleetBenchMarkingChartSettings, InsightIndicatorModel, InsightModel, RightShipInspectionNonConformityModel, RightShipInspectionStatusEnum, UltrasonicReport } from "../../../models/rightship-inspection.models";
import { DocumentManagementDialogConstants } from '@rightship-tech/document-management-app/constants/dialog.constants';
import { DocumentManagementApi } from '@rightship-tech/document-management-app/apis/document-management.api';
import { NumberHelper } from '@rightship-tech/common-ui/helpers/number.helper';

@transient()
@autoinject
export class PartialRightshipInspectionReport {

    public rightShipInspectionId: number;
    public nonConformities: RightShipInspectionNonConformityModel[];
    public totalFindingsCount: number = 0;
    public isLoading: boolean = false;
    public validUntil: string;
    public validUntilText: string;
    public statusName: string;
    public statusId: number;
    public summary: string;
    public chartData: { name: string, totalValue: number, values: { color: string, value: number }[] }[];
    public fleetBenchMarkingChartData: FleetBenchMarkingChartData[];
    public riskLevelHighCount: number;
    public riskLevelMediumCount: number;
    public riskLevelLowCount: number;
    riskLevelHighActive: boolean;
    riskLevelMediumActive: boolean;
    riskLevelLowActive: boolean;
    filteredNonConformities: RightShipInspectionNonConformityModel[];
    public selectedFilter: string[] = [];
    public mobileSelectedFilter: number[] = [];
    public chartSettings: {
        maxValue: number;
        step: number;
        numberOfSteps: number;
        legends: {
            name: string;
            value: string[];
        }[];
    };
    public fleetBenchMarkingChartSettings: any;
    public inspectionBeforeFleetBenchmarkStartDate: boolean;
    public hideShareButton: boolean = true;
    public unacceptable: number = RightShipInspectionStatusEnum.Unacceptable;
    public blob: string = ConfigService.instance().config.urls.blobStorageUrl;
    public totalFindings: number = 0;
    public readonly regulatoryColors: string[] = ["#8A0F19", "#CA4F00", "#EAA80D"];
    public readonly industryStandardColors: string[] = ["#b96f75", "#df9566", "#f2cb6e"];
    public readonly levels = ["High", "Medium", "Low"];
    public ratings: string[] = [
        'High Risk',
        'Medium Risk',
        'Low Risk'
    ];

    public indicators: InsightIndicatorModel[];
    public insights: InsightModel[];
    public loading: {
        riskLevelChart: boolean;
        insights: boolean;
    } = {
            riskLevelChart: false,
            insights: false
        };
    public selectedIndicator: number = 1;
    public emails: string[] = [];
    public vesselName: string;
    public closeoutType: string;
    public openFindings: (questionNumber?: string) => void;
    private _userCustomerId: number;
    private _rightshipInspectionCustomerId: number;
    private _isSmallScreen: boolean;
    private _preview: boolean;
    public showFleetBenchmarking: boolean;
    public ultraSocicInspectionReports: UltrasonicReport[] = [];

    @computedFrom('fleetBenchMarkingChartData')
    get ratingFindingsData() {
        if (!this.fleetBenchMarkingChartData) return null;

        //return findings data as a dictionary
        return this.ratings.reduce((dict, item) => {
            dict[item] = this.getRatingFindingsData(item);
            return dict;
        }, {} as { [key: number]: any[] });
    }

    constructor(private rightshipInspectionService: RightShipInspectionsService, private dialogService: DialogClientService,
        private attachmentService: AttachmentsClientService, private bindingSignaler: BindingSignaler,
        private documentManagementApi: DocumentManagementApi, private customerService: CustomerService,
        private userClaimsService: UserClaimsService) {
    }

    activate(model: {
        inspectionId: number, validUntil: string, validUntilText: string, insightIndicators: InsightIndicatorModel[],
        statusName: string, statusId: number, summary: string, customerId: number, preview: boolean, closeoutType: string, openFindings: (questionNumber: string) => void
    }) {
        this.showFleetBenchmarking = !this.userClaimsService.hasPermission("HideFleetBenchmarking");
        this.rightShipInspectionId = model.inspectionId;
        this.validUntil = model.validUntil;
        this.validUntilText = model.validUntilText;
        this.statusName = model.statusName;
        this.statusId = model.statusId;
        this.summary = model.summary;
        this._rightshipInspectionCustomerId = model.customerId;
        this._preview = model.preview;
        this.closeoutType = model.closeoutType;
        if (model.insightIndicators) {
            this.indicators = model.insightIndicators;
            if (this.indicators && this.indicators.length > 0) {
                this.selectIndicator(this.indicators[0].id);
            }
        }
        this.openFindings = model.openFindings;
    }

    async attached() {
        if (this.shouldShowPreview) {
            this.loading.riskLevelChart = true;
            this.initUltrasocicReport();
            await this.getCurrentCustomer();
            await this.getUsersAccess();
            await this.getChartInfo();
            await this.refreshChart();
            window.addEventListener('resize', this.refreshChart);
            await this.initFleetBenchMarkingChartData();
            if (!this.indicators) await this.getInsights();
        }
    }

    async initUltrasocicReport() {
        const result = await this.rightshipInspectionService.getUltrasonicReport(this.rightShipInspectionId);
        this.ultraSocicInspectionReports = result.ultrasonicReports ?? [];
    }

    detached() {
        window.removeEventListener('resize', this.refreshChart);
        this.indicators = null;
        this.fleetBenchMarkingChartData = null;
    }

    refreshChart = async () => {
        this.bindingSignaler.signal('update-chart');
        this.checkScreenSize();
    }

    checkScreenSize() {
        this._isSmallScreen = window.innerWidth <= 768;
    }

    @computedFrom('statusName')
    get shouldShowPreview() {
        return this.statusId == RightShipInspectionStatusEnum.Acceptable || this.statusId == RightShipInspectionStatusEnum.Unacceptable;
    }

    @computedFrom('_userCustomerId')
    get shouldShowAccessReport() {
        return this._userCustomerId === this._rightshipInspectionCustomerId;
    }

    selectIndicator(id: number) {
        this.selectedIndicator = id;
        this.insights = this.indicators.find(x => x.id == id)?.insights;
    }

    openFindingsTab(questionNumber?: string) {
        if (this.openFindings) {
            this.openFindings(questionNumber);
        }
    }

    getBarSize(value: number, maxValue: number, containerSize: number) {
        let result = 0;

        if (value >= maxValue) {
            return 0;
        }

        const valuePercentage = value / maxValue;
        result = containerSize * valuePercentage;

        return result;
    }

    public riskLevelFilters: { name: string, riskLevelId: number }[] = [
        {
            name: 'High',
            riskLevelId: FindingRiskLevelEnum.High
        }, {
            name: 'Medium',
            riskLevelId: FindingRiskLevelEnum.Medium
        }, {
            name: 'Low',
            riskLevelId: FindingRiskLevelEnum.Low
        }
    ];

    async getCurrentCustomer() {
        this._userCustomerId = await this.customerService.getCustomerId();
    }

    async getChartInfo() {
        const data = await this.rightshipInspectionService.getRSInspectionOutcome(this.rightShipInspectionId);
        if (data) {
            this.initChartData(data);
            this.vesselName = data.vesselName;
            this.loading.riskLevelChart = false;
        }

        this.hideShareButton = !data.allowShareOutcome;
    }

    async getInsights() {
        try {
            this.loading.insights = true;
            const getInsightResponse = await this.rightshipInspectionService.getInsights(this.rightShipInspectionId);
            this.indicators = getInsightResponse.insightIndicators;

            if (this.indicators && this.indicators.length > 0) {
                this.selectIndicator(this.indicators[0].id);
            }

            this.loading.insights = false;
        } catch (error) {
            this.loading.insights = false;
            console.error(error);
        }
    }

    async getUsersAccess() {
        if (this.shouldShowAccessReport) {
            try {
                const users = await this.documentManagementApi.listRightshipInspectionDocumentAccess(this.rightShipInspectionId);
                this.emails = users.accesses[0].emails;
            } catch (error) {
                console.error(error);
            }
        }
    }

    /**
     * Get risk level count
     * @param riskLevelName risk level name
     */
    getCount(riskLevelName: string) {
        if (riskLevelName == 'High' && this.riskLevelHighCount)
            return this.riskLevelHighCount;
        if (riskLevelName == 'Medium' && this.riskLevelMediumCount)
            return this.riskLevelMediumCount;
        if (riskLevelName == 'Low' && this.riskLevelLowCount)
            return this.riskLevelLowCount;
        return 0;
    }

    riskLevelHighClick() {
        this.riskLevelHighActive = !this.riskLevelHighActive;
        this.riskLevelLowActive = false;
        this.riskLevelMediumActive = false;

        this.setFilteredNonConformities();
    }

    riskLevelMediumClick() {
        this.riskLevelMediumActive = !this.riskLevelMediumActive;
        this.riskLevelLowActive = false;
        this.riskLevelHighActive = false;

        this.setFilteredNonConformities();
    }

    riskLevelLowClick() {
        this.riskLevelLowActive = !this.riskLevelLowActive;
        this.riskLevelHighActive = false;
        this.riskLevelMediumActive = false;

        this.setFilteredNonConformities();
    }

    setFilteredNonConformities() {
        if (this.riskLevelHighActive) {
            this.filteredNonConformities = this.nonConformities.filter((x) => (x.riskLevelId ? x.riskLevelId : x.defaultRiskLevelId) == FindingRiskLevelEnum.High);
        }
        else if (this.riskLevelMediumActive) {
            this.filteredNonConformities = this.nonConformities.filter((x) => (x.riskLevelId ? x.riskLevelId : x.defaultRiskLevelId) == FindingRiskLevelEnum.Medium);
        }
        else if (this.riskLevelLowActive) {
            this.filteredNonConformities = this.nonConformities.filter((x) => (x.riskLevelId ? x.riskLevelId : x.defaultRiskLevelId) == FindingRiskLevelEnum.Low);
        }
        else {
            this.filteredNonConformities = this.nonConformities;
        }
        return this.filteredNonConformities;
    }

    clearAllFilters() {
        this.riskLevelHighActive = false;
        this.riskLevelMediumActive = false;
        this.riskLevelLowActive = false;

        return this.setFilteredNonConformities();
    }

    getInsightTitle(title: string, count: number) {
        return `<div class="insight-title">
            <span class="title">${title}</span> 
            <span class="count">(${count != 1 ? count + ' findings' : count + ' finding'})</span>
        </div>`
    }

    //Initialize the chartData and chartSettings properties
    initChartData(data) {
        this.chartData = [];
        if (data && (data.regulatory.length > 0 || data.industryStandard.length > 0)) {
            for (let i = 0; i < this.levels.length; i++) {
                const regulatoryValue = data.regulatory && data.regulatory[i] ? data.regulatory[i] : 0;
                const industryStandardValue = data.industryStandard && data.industryStandard[i] ? data.industryStandard[i] : 0;
                const totalValue = regulatoryValue + industryStandardValue;

                this.chartData.push({
                    name: this.levels[i],
                    values: [
                        {
                            color: this.regulatoryColors[i],
                            value: regulatoryValue
                        },
                        {
                            color: this.industryStandardColors[i],
                            value: industryStandardValue
                        }
                    ],
                    totalValue: totalValue
                });
            }
            this.totalFindings = this.chartData.reduce((acc, curr) => acc + curr.totalValue, 0);
            this.calculateChartSettings();
        }
    }

    calculateChartSettings() {
        let highestTotalValue = 0;
        // Find the highest totalValue in the chartData
        this.chartData.forEach(item => {
            item.values.forEach(value => {
                if (value.value > highestTotalValue) highestTotalValue = value.value;
            })
        });
        // Calculate maxValue, step, and numberOfSteps
        const step = Math.ceil(highestTotalValue / 3); // Adjust the divisor to control step size
        const maxValue = step * 4; // Ensure one more step higher than the highest totalValue
        const numberOfSteps = 4;

        // Update chart settings
        this.chartSettings = {
            maxValue: maxValue,
            step: step,
            numberOfSteps: numberOfSteps,
            legends: [
                {
                    name: "Regulatory",
                    value: this.regulatoryColors
                },
                {
                    name: "Industry standard",
                    value: this.industryStandardColors
                }
            ]
        };
    }

    async initFleetBenchMarkingChartData() {

        const data = await this.rightshipInspectionService.getRightshipInspectionFleetBenchMarkingChart(this.rightShipInspectionId, this._preview);

        const findings = [
            this.vesselName,
            "DOC Fleet",
            "World Fleet"
        ]
        let maxValue = data.max;
        maxValue = data.max + 5;
        const numberOfSteps = Math.ceil(maxValue / 5);
        this.fleetBenchMarkingChartSettings = this.generateChartSettings(maxValue, numberOfSteps);

        this.fleetBenchMarkingChartSettings.legends = this.getChartLegend(findings);
        this.fleetBenchMarkingChartSettings.note = `Benchmarking for this vessel is filtered by similar type (${data.vesselType})) and size (${NumberHelper.getFormattedValue(data.minDwt, null, 0)}-${NumberHelper.getFormattedValue(data.maxDwt, null, 0)} DWT). The comparisons for My Fleet and World Fleet are based on median values from RISQ Inspections closed from ${data.rsInspFleetBenchmarkStartDate}.`;
        this.inspectionBeforeFleetBenchmarkStartDate = data.inspectionBeforeFleetBenchmarkStartDate;
        this.fleetBenchMarkingChartData = data.charts;
    }

    generateChartSettings(value: number, numberOfSteps: number = 10): FleetBenchMarkingChartSettings {
        const settings: FleetBenchMarkingChartSettings = {
            maxValue: 0,
            step: 0,
            numberOfSteps: numberOfSteps,
            legends: []
        };

        const maxValue = this.computeMaxValue(value);
        const step = Number((maxValue / numberOfSteps).toFixed(0));

        settings.maxValue = Number(maxValue.toFixed(0));
        settings.step = step;
        return settings;
    }

    getChartLegend(namesToConvert: string[]): FleetBenchMarkingChartLegend[] {
        if (!namesToConvert || namesToConvert.length == 0)
            return [];
        const legend: FleetBenchMarkingChartLegend[] = [];
        namesToConvert.forEach((name, index) => {
            legend.push({ name: name, cssClass: `color${index + 1}` })
        });
        return legend;
    }

    computeMaxValue(value: number): any {
        let maxValue = value;
        let multiplier = 1;
        let ceilingValue = 100;
        if (maxValue <= 100) {
            ceilingValue = 10;
        }

        while (maxValue > ceilingValue) {
            maxValue = maxValue / 10;
            multiplier = multiplier * 10;
        }

        maxValue = Math.ceil(maxValue) * multiplier;

        // min number of steps is 10 with step of 1
        if (maxValue < 10)
            maxValue = 10;

        return maxValue;
    }

    getRatingFindingsData(rating: string): any[] {
        if (!this.fleetBenchMarkingChartData)
            return [];

        const filteredData = this.fleetBenchMarkingChartData.filter(x => x.rating == rating)[0];

        if (!filteredData)
            return [];

        return filteredData.findings;
    }

    getWidth(value: number, color: string, maxValue: number, container: HTMLElement, periodData: { totalValue: number, values: { color: string, value: number }[] }) {
        const containerWidth = (container.clientWidth - 16) / this.chartSettings.numberOfSteps;
        const baseWidth = this._isSmallScreen ? 100 : 48;
        const effectiveWidth = containerWidth <= baseWidth ? baseWidth : containerWidth;
        const width = this.getIntensityBarSize(value, maxValue, effectiveWidth * this.chartSettings.numberOfSteps, periodData);
        return `width: ${width}px; background-color: ${color}`;
    }

    getIntensityBarSize(value: number, maxValue: number, containerSize: number, periodData: { totalValue: number, values: { color: string, value: number }[] }): number {
        //compute % of average vs max average
        const averagePercentage = periodData.totalValue / maxValue;
        //compute container size for average
        const baseContainerSize = containerSize * averagePercentage;
        //compute total value
        let total = Enumerable.from(periodData.values).sum(x => x.value);
        total = total ? total : 1;
        //compute % of value vs total
        const valuePercentage = value / total;
        //compute bar size
        const result = baseContainerSize * valuePercentage;
        return result;
    }

    getBarCssClass(name: string): string {
        return name
            .toLowerCase()
            .replace(/\s+/g, '-')
            .replace(/[^a-z0-9-]/g, '');
    }

    getBenchmarkingBarCssClass(legends: FleetBenchMarkingChartLegend[], name: string): string {
        let result = '';
        result = Enumerable.from(legends).where(x => x.name == name).select(x => x.cssClass).firstOrDefault();
        return result;
    }

    getNonConformities() {
        this.isLoading = true;
        return new Promise<void>((resolve, reject) => {
            this.rightshipInspectionService.getRSInspectionNonConformity(this.rightShipInspectionId).then(x => {
                this.nonConformities = Enumerable.from(x.nonConformities).orderBy(resp => resp.sectionSequence).toArray();
                this.totalFindingsCount = this.nonConformities.length;
                this.riskLevelHighCount = this.nonConformities.filter(x => (x.riskLevelId ? x.riskLevelId : x.defaultRiskLevelId) == FindingRiskLevelEnum.High).length;
                this.riskLevelMediumCount = this.nonConformities.filter(x => (x.riskLevelId ? x.riskLevelId : x.defaultRiskLevelId) == FindingRiskLevelEnum.Medium).length;
                this.riskLevelLowCount = this.nonConformities.filter(x => (x.riskLevelId ? x.riskLevelId : x.defaultRiskLevelId) == FindingRiskLevelEnum.Low).length;
                this.isLoading = false;
                this.setFilteredNonConformities();
                this.isLoading = false;
                resolve();
            }, e => {
                console.error(e);
                this.isLoading = false;
                reject(e);
            });
        });
    }

    getFormattedDate(date: string) {
        return moment(date).format('DD MMM YYYY');
    }

    getRiskLevelClass(conformity: RightShipInspectionNonConformityModel) {
        const riskLevel = conformity.riskLevelId ? conformity.riskLevelId : conformity.defaultRiskLevelId;
        switch (riskLevel) {
            case 1:
                return "High";
            case 2:
                return "Medium";
            case 3:
            default:
                return "Low";
        }
    }

    private isXls(link): boolean {
        const extensions = ["xls"];
        return extensions.some(extension => link.toLowerCase().indexOf(extension) != -1);
    }

    private isDoc(link): boolean {
        const extensions = ["doc"];
        return extensions.some(extension => link.toLowerCase().indexOf(extension) != -1);
    }

    private isPDF(link): boolean {
        return link?.toLowerCase()?.indexOf('.pdf') != -1;
    }

    private isImage(link): boolean {
        const imageExtensions = ["jpg", "jpeg", "png", "gif", "svg", "heic"];
        const lowerCaseLink = link.toLowerCase();
        return imageExtensions.some(imageExtension => lowerCaseLink.indexOf(imageExtension) != -1);
    }

    async openFile(file) {
        if (file && file.path) {
            if (!file.fullUrl) {
                await this.addAttachmentToken(file);
            }
            const path = file.fullUrl;
            const downloadFile = !(this.isPDF(path) || this.isImage(path));


            if (!downloadFile) {
                this.dialogService.show(CommonUiDialogConstants.ObjectViewer,
                    {
                        link: path,
                        doNotAddExtension: true
                    }, false, false, true);
            }
            else {
                FileHelper.downloadFile(AttachmentsHelper.getUrl(path, this.blob, null), file.documentName);
            }
        }
    }

    async getFileUrl(file) {
        if (!file || !file.path) {
            return require('../../../images/icons/icon-document.svg');
        }
        if (this.isImage(file.path)) {
            if (!file.fullUrl) await this.addAttachmentToken(file);
            if (file.fullUrl) {
                return file.fullUrl;
            }
        }
        if (this.isPDF(file.path)) {
            return require('../../../images/rs-inspection/rs-inspection-pdf.png');
        }
        if (this.isXls(file.path)) {
            return require('../../../images/rs-inspection/rs-inspection-xls.png');
        }
        if (this.isDoc(file.path)) {
            return require('../../../images/rs-inspection/rs-inspection-doc.png');
        }
        return require('../../../images/icons/icon-document.svg');
    }

    addAttachmentToken(file) {
        return new Promise<string>((resolve, reject) => {
            if (file && file.path) {
                let path = AttachmentsHelper.getUrl(file.path, this.blob, null);
                const downloadFile = !(this.isPDF(path) || this.isImage(path));
                this.attachmentService.getSharedAccessSignatureToken(file.entityAttachmentId, downloadFile).then(sasToken => {
                    if (sasToken) {
                        path += ((sasToken.startsWith("?") ? "" : "?") + sasToken);
                    }

                    file.fullUrl = AttachmentsHelper.getUrl(path, this.blob, null);

                    resolve(file.fullUrl);
                }).catch(e => reject(e));
            }
        });
    }

    openShareDialog() {
        this.dialogService.show(DocumentManagementDialogConstants.ShareOutcomDialog, {
            rightshipInspectionId: this.rightShipInspectionId,
            refreshEmails: this.refreshEmails.bind(this),
            emails: this.emails
        }, true, false, false, "right");
    }

    manageAccess() {
        this.dialogService.show(DocumentManagementDialogConstants.ManageAccesShareOutcomeDialog, { rightshipInspectionId: this.rightShipInspectionId, refreshEmails: this.refreshEmails.bind(this) },
            true, false, false, "right");
    }

    refreshEmails(emails: string[]) {
        this.emails = emails;
    }
}